import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function JoinPrivateChallenge() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState("");

  const joinMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Find challenge by invite code
      const { data: challenge, error: findError } = await supabase
        .from('group_challenges')
        .select('id, title, is_private')
        .eq('invite_code', inviteCode.toUpperCase())
        .eq('is_private', true)
        .single();

      if (findError || !challenge) {
        throw new Error("Invalid invite code");
      }

      // Check if already joined
      const { data: existing } = await supabase
        .from('challenge_participants')
        .select('id')
        .eq('challenge_id', challenge.id)
        .eq('user_id', user.id)
        .single();

      if (existing) {
        throw new Error("You've already joined this challenge");
      }

      // Join the challenge
      const { error: joinError } = await supabase
        .from('challenge_participants')
        .insert({
          challenge_id: challenge.id,
          user_id: user.id,
          completion_status: 'active',
        });

      if (joinError) throw joinError;

      return challenge;
    },
    onSuccess: (challenge) => {
      queryClient.invalidateQueries({ queryKey: ['active-challenges'] });
      toast.success(`Joined "${challenge.title}"!`);
      setOpen(false);
      setInviteCode("");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to join challenge");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Lock className="h-4 w-4" />
          Join with Code
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Join Private Challenge</DialogTitle>
          <DialogDescription>
            Enter the invite code shared by the challenge creator
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">Invite Code</Label>
            <Input
              id="code"
              placeholder="Enter 8-character code"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              maxLength={8}
              className="uppercase font-mono text-center text-lg tracking-widest"
            />
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => joinMutation.mutate()}
              disabled={inviteCode.length !== 8 || joinMutation.isPending}
              className="flex-1"
            >
              Join Challenge
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
